/**
 * protein78 genel davranışlar (snippets/cd-head.liquid yükler; type="module" = ertelenmiş).
 *
 * 1) Mağaza durumu (blocks/cd-store-status.liquid): Türkiye saatine göre bugünün saatini ve açık/kapalı noktasını yazar.
 * 2) Header sepet tutarı (snippets/cd-cart-total.liquid): sepet güncellenince /cart.js ile tutarı yeniler.
 * 3) Yüzen WhatsApp (sections/cd-whatsapp-float.liquid): çerez bandı, açık dialog ya da mobil menü varken gizler.
 *
 * Bağımlılık eklemez; Horizon'un @theme/money-formatting modülünü ve Shopify standart olaylarını kullanır.
 */

const ISTANBUL = 'Europe/Istanbul';
const GUNLER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** @returns {{ gun: number, dakika: number }} Pazartesi = 0 */
function istanbulSimdi() {
  const parcalar = new Intl.DateTimeFormat('en-GB', {
    timeZone: ISTANBUL,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  /** @type {Record<string, string>} */
  const p = {};
  for (const parca of parcalar) p[parca.type] = parca.value;
  return {
    gun: Math.max(0, GUNLER.indexOf(p.weekday ?? 'Mon')),
    dakika: Number(p.hour ?? 0) * 60 + Number(p.minute ?? 0),
  };
}

/**
 * "10:00–20:00" → [600, 1200]; çözülemezse null
 * @param {string} metin
 */
function saatAraligi(metin) {
  const eslesme = metin.match(/(\d{1,2})[:.](\d{2})\s*[–—-]\s*(\d{1,2})[:.](\d{2})/);
  if (!eslesme) return null;
  const [, a1, a2, b1, b2] = eslesme.map(Number);
  return [a1 * 60 + a2, b1 * 60 + b2];
}

function magazaDurumu() {
  const ogeler = document.querySelectorAll('[data-cd-store-status]');
  if (!ogeler.length) return;
  const { gun, dakika } = istanbulSimdi();

  for (const oge of ogeler) {
    if (!(oge instanceof HTMLElement)) continue;
    const saatler = (oge.dataset.cdSaatler ?? '').split('|');
    if (saatler.length !== 7) continue;
    const bugun = (saatler[gun] ?? '').trim();
    const hedef = oge.querySelector('[data-cd-store-today]');
    if (!hedef) continue;

    if (!bugun || bugun === '-') {
      hedef.textContent = oge.dataset.cdKapali || hedef.textContent;
      oge.dataset.cdAcik = 'false';
      continue;
    }

    hedef.textContent = `${oge.dataset.cdBugun ?? ''} ${bugun}`.trim();
    const aralik = saatAraligi(bugun);
    oge.dataset.cdAcik = aralik && dakika >= aralik[0] && dakika < aralik[1] ? 'true' : 'false';
  }
}

/* ---------- Sepet tutarı ---------- */

/** @type {AbortController | null} */
let sepetIstegi = null;

async function sepetTutariniYenile() {
  const ogeler = document.querySelectorAll('[data-cd-cart-total]');
  if (!ogeler.length) return;

  sepetIstegi?.abort();
  sepetIstegi = new AbortController();

  try {
    const kok = window.Shopify?.routes?.root ?? '/';
    const yanit = await fetch(`${kok}cart.js`, {
      signal: sepetIstegi.signal,
      headers: { Accept: 'application/json' },
    });
    if (!yanit.ok) return;
    const sepet = await yanit.json();
    const { formatMoney } = await import('@theme/money-formatting');

    for (const oge of ogeler) {
      if (!(oge instanceof HTMLElement)) continue;
      const deger = oge.querySelector('[data-cd-cart-total-value]');
      if (!deger) continue;
      const bicim = oge.dataset.moneyFormat || '{{amount_with_comma_separator}} TL';
      const para = sepet.currency || oge.dataset.currency || 'TRY';
      deger.textContent = formatMoney(Number(sepet.total_price ?? 0), bicim, para);
    }
  } catch (hata) {
    if (hata instanceof Error && hata.name !== 'AbortError') console.warn('[cd-global] sepet tutarı:', hata);
  }
}

async function sepetOlaylariniDinle() {
  if (!document.querySelector('[data-cd-cart-total]')) return;

  try {
    const { StandardEvents } = await import('@shopify/events');
    document.addEventListener(StandardEvents.cartLinesUpdate, (olay) => {
      const vaat = /** @type {{ promise?: Promise<unknown> }} */ (olay).promise;
      if (vaat && typeof vaat.then === 'function') {
        vaat.then(sepetTutariniYenile, () => {});
      } else {
        sepetTutariniYenile();
      }
    });
  } catch (hata) {
    console.warn('[cd-global] standart olaylar yüklenemedi:', hata);
  }

  window.addEventListener('pageshow', (olay) => {
    if (olay.persisted) sepetTutariniYenile();
  });
}

/* ---------- Yüzen WhatsApp: tek katman ---------- */

function cerezBandiAcik() {
  const bant = document.getElementById('shopify-pc__banner');
  if (!bant) return false;
  const stil = getComputedStyle(bant);
  return stil.display !== 'none' && stil.visibility !== 'hidden' && bant.getClientRects().length > 0;
}

function waDurumunuGuncelle() {
  const gizle =
    cerezBandiAcik() ||
    document.querySelector('dialog[open]') !== null ||
    document.querySelector('#Details-menu-drawer-container[open]') !== null;
  document.body.toggleAttribute('data-cd-wa-gizle', gizle);
}

function waKatmaniniIzle() {
  if (!document.querySelector('[data-cd-wa]')) return;

  let bekleyen = 0;
  const planla = () => {
    if (bekleyen) return;
    bekleyen = requestAnimationFrame(() => {
      bekleyen = 0;
      waDurumunuGuncelle();
    });
  };

  /** @type {Element | null} */
  let izlenenBant = null;
  const bantGozlemcisi = new MutationObserver(planla);
  const bantiBagla = () => {
    const bant = document.getElementById('shopify-pc__banner');
    if (!bant || bant === izlenenBant) return;
    bantGozlemcisi.disconnect();
    izlenenBant = bant;
    bantGozlemcisi.observe(bant, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
  };

  // Dialog / details açılıp kapanması ("open") ve body'ye eklenen çerez bandı
  // (aynı gözlemci aynı düğümü iki kez izlerse ayarlar birbirini ezer; bu yüzden iki ayrı gözlemci)
  new MutationObserver(planla).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  new MutationObserver(() => {
    bantiBagla();
    planla();
  }).observe(document.body, { childList: true });
  bantiBagla();

  document.addEventListener('visitorConsentCollected', planla);
  waDurumunuGuncelle();
}

/* ---------- Başlat ---------- */

function baslat() {
  magazaDurumu();
  sepetOlaylariniDinle();
  waKatmaniniIzle();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', baslat, { once: true });
} else {
  baslat();
}

/* Tema editöründe bölüm/blok yeniden çizilince mağaza durumunu tazele */
document.addEventListener('shopify:section:load', magazaDurumu);
document.addEventListener('shopify:block:select', magazaDurumu);
